import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router";
import { flyToBag } from "../components/fly.ts";
import { Icon } from "../components/Icon.tsx";
import { Mockup } from "../components/Mockup.tsx";
import { findVariant, initialSelection, OptionPicker, Quantity } from "../components/Options.tsx";
import { useConfig } from "../components/Shell.tsx";
import { Sticker } from "../components/Sticker.tsx";
import { ApiError, api, call } from "../lib/api.ts";
import { cartStore } from "../lib/cart.ts";
import { isColorOption, money, swatchFor } from "../lib/format.ts";
import { pixel } from "../lib/pixel.ts";

const TEE_AREA = { x: 0.31, y: 0.25, w: 0.38, h: 0.48 };

import { Description } from "../components/Description.tsx";
import { toast } from "../lib/toast.ts";
import { NotFoundPage } from "./misc.tsx";

export function ProductPage() {
  const { slug = "" } = useParams();
  const cfg = useConfig();
  const currency = cfg.data?.currency ?? "EUR";
  const q = useQuery({
    queryKey: ["product", slug],
    queryFn: () => call(api.products[":slug"].$get({ param: { slug } })),
  });
  const [selected, setSelected] = useState<Record<string, string> | null>(null);
  const [qty, setQty] = useState(1);
  const [tried, setTried] = useState(false);
  const stickerRef = useRef<HTMLDivElement>(null);

  const viewed = q.data?.product;
  // biome-ignore lint/correctness/useExhaustiveDependencies: one view per product, not per refetch
  useEffect(() => {
    if (viewed) pixel.viewContent(viewed.id, viewed.title, viewed.priceCents);
  }, [viewed?.id]);

  if (q.error instanceof ApiError && q.error.status === 404) return <NotFoundPage />;
  if (q.isLoading || !q.data) {
    return (
      <div className="sheet pdp">
        <div className="skeleton" style={{ aspectRatio: "1" }} />
        <div className="stack">
          <div className="skeleton" style={{ height: 80 }} />
          <div className="skeleton" style={{ height: 200 }} />
        </div>
      </div>
    );
  }
  const p = q.data.product;
  // An old link to a renamed product: move to its current URL.
  if (p.slug !== slug) return <Navigate to={`/p/${p.slug}`} replace />;
  const sel = selected ?? initialSelection(p.variants);
  const variant = findVariant(p.variants, sel);
  const missing = p.options.map((o) => o.name).filter((n) => !sel[n]);
  const colorName = Object.entries(sel).find(([k]) => isColorOption(k))?.[1];

  const add = () => {
    setTried(true);
    if (!variant) return;
    cartStore.add({
      line: { kind: "catalog", variantId: variant.id, quantity: qty },
      title: p.title,
      variantTitle: variant.title,
      imageUrl: p.images[0] ?? null,
      unitPriceCents: variant.priceCents,
      href: `/p/${p.slug}`,
    });
    flyToBag(stickerRef.current, p.images[0] ?? null);
    toast(`Added to your bag`, { action: { label: "View bag", href: "/bag" } });
  };

  return (
    <div className="sheet pdp">
      <div ref={stickerRef} className="pdp__media">
        <Sticker face="var(--sky)" tilt={-2} peelable>
          {p.artworkUrl ? (
            // Flat artwork known: draw it on a mock-up in the colour being chosen.
            <Mockup
              spec={{ shape: p.category === "apparel" ? "tee" : "poster", area: TEE_AREA }}
              color={swatchFor(colorName) ?? "#1A1A1A"}
              aspect={{ width: 1000, height: 1000 }}
              label={`${p.title}${colorName ? ` in ${colorName}` : ""}`}
              className="pdp__mockup"
            >
              <image
                href={p.artworkUrl}
                width={1000}
                height={1000}
                preserveAspectRatio="xMidYMin meet"
              />
            </Mockup>
          ) : (
            p.images[0] && <img src={p.images[0]} alt={p.title} className="pdp__img" />
          )}
        </Sticker>
      </div>
      <div className="pdp__info">
        <Link to="/shop" className="backlink">
          <Icon name="arrow-left" size={18} /> All products
        </Link>
        <h1 className="pdp__title">{p.title}</h1>
        <p className="pdp__price price">{money(variant?.priceCents ?? p.priceCents, currency)}</p>
        <Description html={p.descriptionHtml} className="pdp__desc" />

        <OptionPicker variants={p.variants} selected={sel} onSelect={setSelected} />
        {tried && !variant && (
          <p className="form-error" role="alert">
            Choose a {missing.join(" and ").toLowerCase() || "different option"} first.
          </p>
        )}
        <div className="pdp__buy">
          <Quantity value={qty} onChange={setQty} label="Quantity" />
          <button type="button" className="btn pdp__add" onClick={add}>
            <Icon name="bag" /> Add to bag
            {variant ? ` · ${money(variant.priceCents * qty, currency)}` : ""}
          </button>
        </div>

        <ol className="contents" aria-label="What you get">
          <li>
            <strong>{p.title}</strong>
            <span>{variant ? variant.title : "Pick your options above"}</span>
          </li>
          <li>
            <strong>Printed on demand</strong>
            <span>Made for you after you order, by our print partners.</span>
          </li>
          <li>
            <strong>Shipping to your address</strong>
            <span>Cost and delivery estimate shown at checkout before you pay.</span>
          </li>
          <li>
            <strong>Paid with PayPal</strong>
            <span>PayPal balance, cards and the other methods PayPal offers you.</span>
          </li>
        </ol>
      </div>
    </div>
  );
}
